import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { Upload, X, Crown, Lightbulb, Film, Trash2 } from 'lucide-react';
import { Loader } from './ui/Loader';
import { motion, AnimatePresence } from 'motion/react';
import { projectId, API_BASE } from '../utils/supabase/info';
import { fetchWithAuth } from '../utils/api';
import { VideoUploader } from './VideoUploader';
import { VideoPlayer } from './VideoPlayer';
import { ConfirmDialog } from './ConfirmDialog';
import { handleApiError } from '../utils/api-error-handler';
import { containsProfanity } from '../utils/profanity-filter'; // ✅ NEW: Profanity filter
import { devLog, devError } from '../utils/logger';
import { canUploadVideo } from '../utils/accessPolicy';
import { toast } from '../utils/toast';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { triggerAndroidHaptic } from '../utils/haptics';
import { compressImageForUpload, PORTFOLIO_PRESET } from '../utils/imageCompression';
import { useImageCropModal } from '../utils/hooks/useImageCropModal';
import { PROJECT_COVER_SPEC_4_3 } from '../utils/media/imageCrop';
import { PostDetailView } from './PostDetailView';
import { FullscreenShell } from './layout/FullscreenShell';


interface CreatePostPageProps {
  accessToken: string | null;
  onPostCreated: () => void;
  onCancel: () => void;
  onNavigateToSubscription?: () => void;
}

export function CreatePostPage({ accessToken, onPostCreated, onCancel, onNavigateToSubscription }: CreatePostPageProps) {
  useModalFocus(true, { fullscreen: true }); // Full-screen: hide header/footer while open
  const { openCrop, cropModal } = useImageCropModal();
  // Editor contract:
  // - cover image is separate from gallery images (max 3)
  // - cancel does not auto-save; navigation-away may auto-save in later phase
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [createdPostId, setCreatedPostId] = useState<string | null>(null);
  const [showVideoUploader, setShowVideoUploader] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false); // ✅ NEW: Separate loading state for video operations
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'publish' | 'draft' | null>(null);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [postCount, setPostCount] = useState(0);
  const [showDeleteVideoConfirm, setShowDeleteVideoConfirm] = useState(false);
  const [videoEncodingStatus, setVideoEncodingStatus] = useState<'unknown' | 'encoding' | 'finished' | 'failed'>('unknown');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [compressingImages, setCompressingImages] = useState(false); // ✅ NEW: Track image compression progress
  const [mediaProcessingMessage, setMediaProcessingMessage] = useState('Behandler bilder...');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const initialSnapshotRef = useRef({
    title: '',
    description: '',
    imageCount: 0,
    hasCover: false,
    hasVideo: false,
  });
  const skipAutoSaveRef = useRef(false);
  const autoSaveInFlightRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const accessTokenRef = useRef<string | null>(accessToken);
  const draftStateRef = useRef({
    title: '',
    description: '',
    coverImagePreview: null as string | null,
    imagePreviews: [] as string[],
    createdPostId: null as string | null,
  });

  // Standardized fullscreen shell uses a fixed header on mobile for all platforms.

  // ✅ NEW: Media loading state tracker (only for uploaded images, no backend images)
  const [mediaLoadingStates, setMediaLoadingStates] = useState({
    coverImage: true, // true = no cover uploaded yet (ready by default)
    images: [] as boolean[] // Empty array - gets populated as images are uploaded
  });

  // ✅ Check if all media is ready
  const allMediaReady = mediaLoadingStates.coverImage && 
                         mediaLoadingStates.images.every(ready => ready);
  const hasUnsavedChanges =
    title.trim() !== initialSnapshotRef.current.title ||
    description.trim() !== initialSnapshotRef.current.description ||
    imagePreviews.length !== initialSnapshotRef.current.imageCount ||
    !!coverImagePreview !== initialSnapshotRef.current.hasCover ||
    !!videoId !== initialSnapshotRef.current.hasVideo ||
    !!createdPostId;
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  accessTokenRef.current = accessToken;
  draftStateRef.current = {
    title,
    description,
    coverImagePreview,
    imagePreviews,
    createdPostId,
  };

  const saveDraftSilently = useCallback(async (
    reason: string,
    options?: { keepalive?: boolean; showToast?: boolean }
  ): Promise<boolean> => {
    const accessTokenValue = accessTokenRef.current;
    if (!accessTokenValue) return false;
    if (autoSaveInFlightRef.current) return false;
    if (!hasUnsavedChangesRef.current) return false;

    autoSaveInFlightRef.current = true;
    setIsSavingDraft(true);
    const loadingToastId = options?.showToast ? toast.loading('Lagrer utkast...') : null;

    try {
      const snapshot = draftStateRef.current;
      const imageBase64 = snapshot.imagePreviews.join(',');
      const payload = {
        title: snapshot.title.trim() || undefined,
        description: snapshot.description.trim() || undefined,
        coverImageBase64: snapshot.coverImagePreview || undefined,
        imageBase64: imageBase64 || undefined,
        isDraft: true,
      };

      const endpoint = snapshot.createdPostId ? `${API_BASE}/posts/${snapshot.createdPostId}` : `${API_BASE}/posts`;
      const method = snapshot.createdPostId ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessTokenValue}`,
        },
        body: JSON.stringify(payload),
        keepalive: options?.keepalive === true,
      });

      if (!response.ok) {
        devError('Auto-save draft failed', { reason, status: response.status });
        return false;
      }

      const data = await response.json().catch(() => null);
      if (!snapshot.createdPostId && data?.post?.id) {
        setCreatedPostId(data.post.id);
        draftStateRef.current.createdPostId = data.post.id;
      }
      if (options?.showToast) {
        toast.dismiss(loadingToastId!);
        toast.success('Utkast lagret');
      }
      return true;
    } catch (error) {
      devError('Auto-save draft error', { reason, error });
      if (options?.showToast) {
        toast.dismiss(loadingToastId!);
        toast.error('Kunne ikke lagre utkast');
      }
      return false;
    } finally {
      if (loadingToastId !== null) {
        toast.dismiss(loadingToastId);
      }
      setIsSavingDraft(false);
      autoSaveInFlightRef.current = false;
    }
  }, []);

  const navigateToSubscriptionWithAutoSave = async () => {
    if (hasUnsavedChangesRef.current) {
      await saveDraftSilently('subscription-link', { showToast: true });
    }
    skipAutoSaveRef.current = true;
    onCancel();
    window.setTimeout(() => {
      if (onNavigateToSubscription) {
        onNavigateToSubscription();
      } else {
        window.location.hash = 'subscription';
      }
    }, 0);
  };

  // ✅ Callbacks from media components
  const handleCoverImageLoad = () => {
    setMediaLoadingStates(prev => ({ ...prev, coverImage: true }));
  };

  const handleImageLoad = (index: number) => {
    setMediaLoadingStates(prev => ({
      ...prev,
      images: prev.images.map((ready, i) => i === index ? true : ready)
    }));
  };
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Refetch when accessToken changes (e.g. after re-login) so profile/posts are not stale
  useEffect(() => {
    if (accessToken) fetchProfileAndPosts();
  }, [accessToken]);

  const fetchProfileAndPosts = async () => {
    try {
      // Get user ID from token
      const response = await fetch(
        `${API_BASE}/profile/me`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        // Fallback: get from current session
        return;
      }

      const data = await response.json();
      setUserProfile(data.profile);

      // Get post count
      const postsResponse = await fetchWithAuth(`${API_BASE}/posts/user/${data.profile.id}`);

      if (postsResponse.ok) {
        const postsData = await postsResponse.json();
        setPostCount(postsData.posts?.length || 0);
      } else {
        const postsError = await postsResponse.json().catch(() => ({}));
        devLog('fetchProfileAndPosts: posts/user fetch failed', {
          status: postsResponse.status,
          requestId: postsError?.requestId ?? postsResponse.headers.get('X-Request-Id') ?? undefined,
          stage: postsError?.stage,
          error: postsError?.error,
        });
      }
    } catch (error) {
      devLog('Error fetching profile:', error);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    const maxImages = 3; // Both free and plus users can upload 3 images
    const remainingSlots = maxImages - imageFiles.length;

    if (remainingSlots <= 0) {
      toast.error('Du kan maksimalt laste opp 3 bilder per prosjekt');
      return;
    }

    // Validate file sizes
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    
    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Bildet "${file.name}" er for stort. Maksimal størrelse er 10MB.`);
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`"${file.name}" er ikke et gyldig bilde.`);
        return;
      }
    }
    
    // Process all files in parallel and batch state update
    try {
      setMediaProcessingMessage('Laster opp bilder...');
      setCompressingImages(true);
      const compressedPreviews = await Promise.all(
        filesToAdd.map(file => compressImageForUpload(file, PORTFOLIO_PRESET))
      );
      
      // Single state update for all images - prevents multiple re-renders
      setImageFiles(prev => [...prev, ...filesToAdd]);
      setImagePreviews(prev => [...prev, ...compressedPreviews]);
      setMediaLoadingStates(prev => ({
        ...prev,
        images: [...prev.images, ...compressedPreviews.map(() => false)] // Mark all new images as not ready
      }));
      setError('');
    } catch (error) {
      devError('Error processing images:', error);
      toast.error('Kunne ikke behandle bildene. Prøv igjen.');
    } finally {
      setCompressingImages(false);
      setMediaProcessingMessage('Behandler bilder...');
    }
  };

  const removeImage = (index: number) => {
    const newImageFiles = imageFiles.filter((_, i) => i !== index);
    const newImagePreviews = imagePreviews.filter((_, i) => i !== index);
    setImageFiles(newImageFiles);
    setImagePreviews(newImagePreviews);
    setMediaLoadingStates(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleCoverImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Cover-bildet er for stort. Maksimal størrelse er 10MB.');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast.error('Cover-bilde må være et gyldig bilde (PNG, JPG, JPEG).');
      return;
    }

    try {
      setMediaProcessingMessage('Laster opp cover-bilde...');
      setCompressingImages(true);
      const croppedFile = await openCrop(file, PROJECT_COVER_SPEC_4_3);
      const compressedPreview = await compressImageForUpload(croppedFile, PORTFOLIO_PRESET);
      setCoverImageFile(croppedFile);
      setCoverImagePreview(compressedPreview);
      setMediaLoadingStates(prev => ({ ...prev, coverImage: false })); // Mark cover image as not ready
      setError('');
    } catch (error) {
      if (error instanceof Error && error.message.includes('avbrutt')) return;
      devError('Error processing cover image:', error);
      toast.error('Kunne ikke behandle cover-bildet. Prøv igjen.');
    } finally {
      setCompressingImages(false);
      setMediaProcessingMessage('Behandler bilder...');
    }
  };

  const removeCoverImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setMediaLoadingStates(prev => ({ ...prev, coverImage: true })); // Mark cover image as ready
  };

  const handleAddVideoClick = async () => {
    // ✅ NEW: NO VALIDATION - Allow video upload anytime (just check Pluss subscription)
    
    // Create a draft post first to get a postId for video upload
    setVideoLoading(true);
    setError('');

    try {
      // Create minimal draft post with isDraft flag
      const response = await fetch(
        `${API_BASE}/posts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim(),
            coverImageBase64: coverImagePreview || '',
            imageBase64: imagePreviews.length > 0 ? imagePreviews.join(',') : '',
            isDraft: true, // ✅ NEW: Mark as draft for minimal validation
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Kunne ikke opprette utkast for video');
        setVideoLoading(false);
        return;
      }

      const postData = await response.json();
      setCreatedPostId(postData.post.id);
      setShowVideoUploader(true);
      setVideoLoading(false);
    } catch (err) {
      devError('Error creating draft post:', err);
      setError('Kunne ikke opprette utkast for video');
      setVideoLoading(false);
    }
  };

  const handleVideoUploaded = (newVideoId: string) => {
    setVideoId(newVideoId);
    setShowVideoUploader(false);
  };

  const handleDeleteVideo = () => {
    if (!videoId) return;
    setShowDeleteVideoConfirm(true);
  };

  const confirmDeleteVideo = async () => {
    setDeletingVideo(true);
    setError('');
    setShowDeleteVideoConfirm(false);

    try {
      const response = await fetch(
        `${API_BASE}/videos/${videoId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke slette video');
      }

      setVideoId(null);
      setShowVideoUploader(false);
      setVideoEncodingStatus('unknown');
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette video');
    } finally {
      setDeletingVideo(false);
    }
  };

  // When we have a videoId, check encoding status so we can show "Video konverterer" if still encoding
  useEffect(() => {
    if (!videoId || !accessToken) {
      setVideoEncodingStatus('unknown');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/videos/${videoId}/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          if (res.status === 422 && errorData?.errorCode === 'VIDEO_TOO_LONG') {
            setVideoEncodingStatus('failed');
            setError(errorData.error || 'Videoen er for lang. Maksimal lengde er 5 minutter.');
            return;
          }
          setVideoEncodingStatus('finished');
          return;
        }
        const data = await res.json();
        const s = data.status;
        if (s === 0 || s === 1 || s === 2) setVideoEncodingStatus('encoding');
        else if (s === 3 || s === 4) setVideoEncodingStatus('finished');
        else if (s === 5) setVideoEncodingStatus('failed');
        else setVideoEncodingStatus('finished');
      } catch {
        if (!cancelled) setVideoEncodingStatus('finished');
      }
    })();
    return () => { cancelled = true; };
  }, [videoId, accessToken]);

  const handleSubmit = async (e?: React.FormEvent) => {
    // Prevent default form submission
    if (e) e.preventDefault();
    
    devLog('🚀 handleSubmit called - starting post creation...');
    devLog('📋 Form data:', { title, description, coverImagePreview: !!coverImagePreview, imagePreviews: imagePreviews.length });
    
    // Keep frontend policy aligned with backend: free users can publish 1, plus users up to 5.
    const maxPosts = canUploadVideo(userProfile) ? 6 : 1;
    if (postCount >= maxPosts) {
      toast.error(
        maxPosts === 1
          ? 'Free-brukere kan ha maks 1 prosjekt. Oppgrader til Plus for opptil 6 prosjekter.'
          : 'Du har nådd maks antall prosjekter for din plan.'
      );
      return;
    }
    
    // Validate required fields
    if (!coverImagePreview) {
      devLog('❌ Validation failed: No cover image');
      toast.error('Cover-bilde er påkrevd');
      return;
    }

    if (!title.trim()) {
      devLog('❌ Validation failed: No title');
      toast.error('Tittel er påkrevd');
      return;
    }

    if (!description.trim()) {
      devLog('❌ Validation failed: No description');
      toast.error('Beskrivelse er påkrevd');
      return;
    }

    // ✅ NEW: Check for profanity in title and description
    if (containsProfanity(title)) {
      toast.error('Tittelen inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }

    if (containsProfanity(description)) {
      toast.error('Beskrivelsen inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }

    // ✅ NEW: Check if all media is ready
    if (!allMediaReady) {
      toast.error('Bilder og cover-bilde må være ferdig lastet før du kan publisere.');
      return;
    }

    devLog('✅ Validation passed - showing publish confirm...');
    setShowPublishConfirm(true);
  };

  /** Faktisk publish-kall etter at bruker har bekreftet i dialog. */
  const doPublish = async () => {
    setShowPublishConfirm(false);
    setLoadingAction('publish');
    setLoading(true);
    setError('');

    try {
      const imageBase64 = imagePreviews.join(',');
      const coverImageBase64 = coverImagePreview;

      if (createdPostId) {
        const response = await fetch(
          `${API_BASE}/posts/${createdPostId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              title,
              description,
              coverImageBase64,
              ...(imageBase64 && { imageBase64 }),
              isDraft: false,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          devError('❌ POST update failed:', errorData);
          handleApiError(errorData, () => {
            void navigateToSubscriptionWithAutoSave();
          });
          setLoadingAction(null);
          setLoading(false);
          return;
        }

        const postData = await response.json();
        devLog('✅ POST updated successfully:', postData);
      } else {
        const response = await fetch(
          `${API_BASE}/posts`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              title,
              description,
              coverImageBase64,
              imageBase64,
              isDraft: false,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          devError('❌ POST creation failed:', errorData);
          handleApiError(errorData, () => {
            void navigateToSubscriptionWithAutoSave();
          });
          setLoadingAction(null);
          setLoading(false);
          return;
        }

        const postData = await response.json();
        devLog('✅ POST created successfully:', postData);
      }

      setLoadingAction(null);
      setLoading(false);
      skipAutoSaveRef.current = true;
      toast.success('Prosjekt publisert!');
      onPostCreated();
    } catch (error) {
      devLog('Error creating post:', error);
      setError('Kunne ikke opprette innlegg');
      setLoadingAction(null);
      setLoading(false);
    }
  };

  // ✅ Save as draft: same profanity rules as publish (content must be clean)
  const handleSaveDraft = async () => {
    devLog('💾 Saving draft...');

    // Profanity check on any provided text (aligned with publish flow)
    const titleToCheck = title.trim();
    const descriptionToCheck = description.trim();
    if (titleToCheck && containsProfanity(titleToCheck)) {
      toast.error('Tittelen inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    if (descriptionToCheck && containsProfanity(descriptionToCheck)) {
      toast.error('Beskrivelsen inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    setLoadingAction('draft');
    setLoading(true);
    setError('');

    try {
      const imageBase64 = imagePreviews.join(',');
      const coverImageBase64 = coverImagePreview;

      const response = await fetch(
        `${API_BASE}/posts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            title: title.trim() || undefined, // Allow empty - backend will use placeholder
            description: description.trim() || undefined,
            coverImageBase64: coverImageBase64 || undefined,
            imageBase64: imageBase64 || undefined,
            isDraft: true, // ✅ Mark as draft - minimal validation on backend
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Kunne ikke lagre utkast');
        setLoadingAction(null);
        setLoading(false);
        return;
      }

      devLog('✅ Draft saved successfully');
      setLoadingAction(null);
      setLoading(false);
      toast.success('Utkast lagret! Du kan fortsette å redigere det senere.');
      skipAutoSaveRef.current = true;
      onPostCreated(); // Close modal and refresh
    } catch (err) {
      devError('Error saving draft:', err);
      setError('Kunne ikke lagre utkast');
      setLoadingAction(null);
      setLoading(false);
    }
  };

  const requestCancel = () => {
    if (loading || videoLoading || deletingVideo || compressingImages || isSavingDraft) return;
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
      return;
    }
    void handleCancel();
  };

  // ✅ NEW: Cleanup handler - Deletes draft post and video when user cancels
  const handleCancel = async () => {
    skipAutoSaveRef.current = true;
    devLog('CreatePost cancel requested', { hasUnsavedChanges });
    // If a draft post was created (for video upload), clean it up
    if (createdPostId) {
      try {
        devLog('🧹 Cleaning up draft post:', createdPostId);
        
        const response = await fetch(
          `${API_BASE}/posts/${createdPostId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          devLog('✅ Draft post and video cleaned up successfully');
        } else {
          devError('⚠️ Failed to cleanup draft post - will be orphaned');
        }
      } catch (err) {
        devError('⚠️ Error during cleanup:', err);
        // Don't block cancel - user wants to exit
      }
    }
    
    // Reset state
    setCreatedPostId(null);
    setVideoId(null);
    
    // Close modal
    onCancel();
  };

  useEffect(() => {
    const handlePageHide = () => {
      if (skipAutoSaveRef.current) return;
      if (!hasUnsavedChangesRef.current) return;
      void saveDraftSilently('pagehide', { keepalive: true });
    };
    const handleBeforeUnload = () => {
      if (skipAutoSaveRef.current) return;
      if (!hasUnsavedChangesRef.current) return;
      void saveDraftSilently('beforeunload', { keepalive: true });
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (!skipAutoSaveRef.current && hasUnsavedChangesRef.current) {
        void saveDraftSilently('component-unmount', { keepalive: true });
      }
    };
  }, []);

  const createPostForm = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Cover Image - FIRST FIELD */}
      <div>
        <label className="block mb-2 text-purple-900 font-medium">Cover-bilde</label>
        <p className="text-sm text-gray-500 mb-3">Dette bildet vises på prosjektkortet.</p>
        {coverImagePreview ? (
          <div className="relative">
            <img src={coverImagePreview} alt="Cover preview" className="w-full aspect-[4/3] object-cover rounded-2xl border-2 border-purple-300" onLoad={handleCoverImageLoad} />
            <button type="button" onClick={removeCoverImage} className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg">
              <X className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-purple-600 bg-opacity-90 text-white rounded-full text-sm font-medium">Cover-bilde</div>
          </div>
        ) : (
          <label htmlFor="cover-image-upload" className="flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-purple-400 rounded-2xl cursor-pointer hover:border-purple-600 hover:bg-purple-50 transition-all">
            <Upload className="w-12 h-12 text-purple-500 mb-2" />
            <p className="text-purple-700 font-medium">Klikk for å laste opp cover-bilde</p>
            <p className="text-purple-400 text-sm">PNG, JPG eller JPEG</p>
            <p className="text-red-600 text-sm mt-2 font-medium">*Påkrevd</p>
            <input id="cover-image-upload" type="file" accept="image/*" onChange={handleCoverImageChange} className="hidden" />
          </label>
        )}
      </div>
      {/* Title */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="title" className="block text-purple-900">Tittel</label>
          <span className={`text-sm ${title.length > 100 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{title.length}/100</span>
        </div>
        <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="F.eks. 'Håndlaget treskulptur' eller 'Moderne nettside for lokalt firma'" required />
      </div>
      {/* Description */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="description" className="block text-purple-900">Beskrivelse</label>
          <span className={`text-sm ${description.length > 1500 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{description.length}/1500</span>
        </div>
        <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1500} className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" rows={5} placeholder="Beskriv prosjektet ditt, hva du gjorde, og hvilke teknikker du brukte..." />
      </div>
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900">Husk å oppdatere profilen din med eventuelle ferdigheter brukt i dette prosjektet.</div>
      {/* Image upload - abbreviated for variable */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-purple-900">Bilder av prosjektet</label>
          <span className="text-gray-500 text-sm">Opptil 3 bilder</span>
        </div>
        {imagePreviews.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative">
                  <img src={preview} alt={`Preview ${index + 1}`} className="w-full max-h-[28rem] object-contain rounded-2xl border-2 border-purple-200 bg-gray-50" onLoad={() => handleImageLoad(index)} />
                  <button type="button" onClick={() => removeImage(index)} className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg">
                    <X className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-3 px-3 py-1 bg-black bg-opacity-60 text-white rounded-full text-sm">Bilde {index + 1}</div>
                </div>
              ))}
            </div>
            {imagePreviews.length < 3 && (
              <label htmlFor="image-upload-more" className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-purple-300 rounded-xl cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all">
                <Upload className="w-5 h-5 text-purple-400" />
                <span className="text-purple-700">Legg til flere bilder</span>
                <input id="image-upload-more" type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
              </label>
            )}
          </div>
        ) : (
          <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-purple-300 rounded-2xl cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all">
            <Upload className="w-12 h-12 text-purple-400 mb-2" />
            <p className="text-purple-700">Klikk for å laste opp bilder</p>
            <p className="text-purple-400 text-sm">PNG, JPG eller JPEG</p>
            <p className="text-gray-500 text-sm mt-2">Opptil 3 bilder per prosjekt</p>
            <input id="image-upload" type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
          </label>
        )}
      </div>
      {/* Video Section - placeholder to keep structure; full section remains in main form below */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-purple-900">Prosjektvideo</label>
          {canUploadVideo(userProfile) && <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full"><Crown className="w-4 h-4 text-white" /><span className="text-white text-sm">Pluss-funksjon</span></div>}
        </div>
        {videoId ? (
          <div>
            {videoEncodingStatus === 'encoding' ? (
              <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl flex flex-col gap-3">
                <p className="text-sm text-amber-900">Video konverterer. Slett og prøv på nytt, eller vent til den er ferdig.</p>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={handleDeleteVideo} disabled={deletingVideo || !canUploadVideo(userProfile)} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                    {deletingVideo ? <Loader size="sm" className="text-white" /> : <Trash2 className="w-4 h-4" />}
                    Slett video
                  </button>
                </div>
              </div>
            ) : videoEncodingStatus === 'failed' ? (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex flex-col gap-3">
                <p className="text-sm text-red-800">Encoding feilet. Du kan slette og prøve på nytt.</p>
                <button type="button" onClick={handleDeleteVideo} disabled={deletingVideo || !canUploadVideo(userProfile)} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                  {deletingVideo ? <Loader size="sm" className="text-white" /> : <Trash2 className="w-4 h-4" />}
                  Slett video
                </button>
              </div>
            ) : (
              <div className="relative">
                <VideoPlayer videoId={videoId} accessToken={accessToken} className="w-full h-64" />
                <button type="button" onClick={handleDeleteVideo} disabled={deletingVideo || !canUploadVideo(userProfile)} className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title={!canUploadVideo(userProfile) ? 'Krever Pluss-abonnement' : 'Slett video'}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            {!canUploadVideo(userProfile) && (
              <div className="mt-3 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-xl flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-800">Du har en video fra Pluss-abonnementet. Oppgrader for å endre eller slette videoen.</p>
                  <button type="button" onClick={() => void navigateToSubscriptionWithAutoSave()} className="mt-2 text-sm text-yellow-700 underline hover:text-yellow-900">Se Pluss-planer</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <button type="button" onClick={() => canUploadVideo(userProfile) ? handleAddVideoClick() : void navigateToSubscriptionWithAutoSave()} disabled={videoLoading || loading || isSavingDraft} className={`w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed rounded-xl transition-all ${canUploadVideo(userProfile) ? 'border-purple-300 hover:border-purple-500 hover:bg-purple-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed' : 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-60'}`}>
              <Film className="w-6 h-6 text-purple-600" />
              <span className="text-purple-700 font-medium">{videoLoading ? 'Klargjør video...' : 'Legg til video'}</span>
              {!canUploadVideo(userProfile) && <Crown className="w-5 h-5 text-yellow-500" />}
            </button>
            {!canUploadVideo(userProfile) && (
              <p className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                <Crown className="w-4 h-4 text-yellow-500" />
                Videoopplasting krever Talentfunn Pluss.
                <button type="button" onClick={() => void navigateToSubscriptionWithAutoSave()} className="text-purple-600 underline hover:text-purple-800">Oppgrader</button>
              </p>
            )}
          </div>
        )}
      </div>
      {error && <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">{error}</div>}
      <div className="flex flex-wrap gap-4 xl:pb-10">
        <button type="submit" onClick={() => triggerAndroidHaptic('light')} disabled={loading || videoLoading} className="flex-1 min-w-[140px] bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">Publiser prosjekt</button>
        <button type="button" onClick={() => { triggerAndroidHaptic('light'); handleSaveDraft(); }} disabled={loading || videoLoading} className="px-6 py-3 border-2 border-purple-300 rounded-xl hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed">Lagre utkast</button>
        <button type="button" onClick={() => { triggerAndroidHaptic('light'); const hasContent = title.trim() || description.trim() || coverImagePreview || imagePreviews.length > 0; if (!hasContent) { toast.info('Legg inn tittel, beskrivelse eller bilder for å forhåndsvise.'); return; } setShowPreview(true); }} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all">Forhåndsvis</button>
        <button type="button" onClick={requestCancel} className="px-6 py-3 border-2 border-purple-300 rounded-xl hover:bg-purple-50 transition-all">Avbryt</button>
      </div>
    </form>
  );
  const createPostFormWithCrop = (
    <>
      {cropModal}
      {createPostForm}
    </>
  );

  return (
    <>
      {/* Loading Overlay - Fullscreen */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center z-[100]"
          >
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full mx-4">
              <div className="flex flex-col items-center gap-6">
                {/* Animated spinner - aria-hidden so only "Lagrer utkast..." etc. is announced */}
                <div className="relative" aria-hidden="true">
                  <Loader size={60} className="text-purple-600" aria-hidden="true" />
                </div>
                
                {/* Loading text */}
                <div className="text-center">
                  <h3 className="text-xl text-gray-900 mb-2">
                    {loadingAction === 'draft' ? 'Lagrer utkast...' : 'Publiserer prosjekt...'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Dette kan ta noen sekunder
                  </p>
                </div>

                {/* Progress animation - aria-hidden so no "loading" is announced under the label */}
                <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden" aria-hidden="true">
                  <motion.div
                    className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 3, ease: "easeInOut" }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        <FullscreenShell
          title="Nytt prosjekt"
          onClose={requestCancel}
          onBack={requestCancel}
          contentClassName="create-edit-post-content"
          overlayMotionProps={{
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
            transition: { duration: 0.18, ease: 'easeOut' },
          }}
        >
          <div className="relative">
            {!loading && compressingImages && (
              <div className="absolute inset-0 talentfunn-modal-overlay backdrop-blur-md rounded-t-3xl xl:rounded-3xl z-50 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-6 shadow-2xl text-center">
                  <Loader size="xl" className="text-purple-600 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">{mediaProcessingMessage}</p>
                  <p className="text-sm text-gray-500 mt-1">Dette tar bare noen sekunder</p>
                </div>
              </div>
            )}
      {createPostFormWithCrop}
          </div>
        </FullscreenShell>
      </AnimatePresence>

      {/* Preview modal – visuell forhåndsvisning, ingen lagring/publisering */}
      {showPreview && (() => {
        const previewPost = {
          id: 'preview',
          title: title.trim() || 'Untitled',
          description: description.trim() || '',
          coverImageUrl: coverImagePreview || undefined,
          imageUrls: imagePreviews.length > 0 ? imagePreviews : undefined,
          videoId: videoId || undefined,
          user: userProfile ? { name: userProfile.name ?? 'Deg', location: userProfile.location, county: userProfile.county } : { name: 'Deg' },
          userId: userProfile?.id,
          createdAt: new Date().toISOString(),
        };
        return (
          <PostDetailView
            post={previewPost}
            onClose={() => setShowPreview(false)}
            accessToken={accessToken}
            currentUserId={userProfile?.id}
            isOwnPost={false}
            isPreview
          />
        );
      })()}

      {/* Video Uploader Modal */}
      {showVideoUploader && createdPostId && (
        <div className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center z-[60] p-4 pb-safe">
          <div className="max-w-2xl w-full">
            <VideoUploader
              accessToken={accessToken}
              projectId={createdPostId}
              onVideoUploaded={handleVideoUploaded}
              onCancel={() => setShowVideoUploader(false)}
            />
          </div>
        </div>
      )}
      
      {/* Publish confirmation – prosjektet blir synlig på profilen */}
      <ConfirmDialog
        isOpen={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        onConfirm={() => void doPublish()}
        title="Publiser prosjekt?"
        message="Når du publiserer prosjektet, blir det synlig på din profil. Er du sikker?"
        confirmText="Publiser"
        cancelText="Avbryt"
        variant="info"
      />

      {/* Delete Video Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteVideoConfirm}
        onClose={() => setShowDeleteVideoConfirm(false)}
        onConfirm={confirmDeleteVideo}
        title="Slett video?"
        message="Er du sikker på at du vil slette videoen?"
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={deletingVideo}
        loadingText="Sletter..."
      />

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          void handleCancel();
        }}
        title="Forkast endringer?"
        message="Du har ulagrede endringer. Er du sikker på at du vil avbryte?"
        confirmText="Ja, avbryt"
        cancelText="Fortsett redigering"
        variant="danger"
      />
    </>
  );
}
